import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axiosInstance from '../api/axiosConfig';
import './ManagementPages.css';
import './ValidationPage.css';
import './VisitDetailPage.css';

// Affectation des hôtesses à leur supermarché. Le compte se crée dans « Gestion
// Utilisateurs » (rôle Hotesse + base) ; ici l'administrateur choisit le point de vente.

function AdminHotessesPage() {
  const [hotesses, setHotesses] = useState([]);
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [edition, setEdition] = useState(null);   // { user_id, base, client_id, recherche }

  const charger = useCallback(async () => {
    setIsLoading(true);
    try {
      const [h, c] = await Promise.all([
        axiosInstance.get('/admin/hotesses'),
        axiosInstance.get('/admin/clients/all'),
      ]);
      setHotesses(h.data);
      setClients(c.data);
    } catch (e) {
      alert("Erreur : impossible de charger les hôtesses.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const suggestions = useMemo(() => {
    const terme = (edition?.recherche || '').trim().toLowerCase();
    if (terme.length < 2) return [];
    return clients
      .filter(c => (c.nom_client || '').toLowerCase().includes(terme))
      .slice(0, 15);
  }, [clients, edition?.recherche]);

  const enregistrer = async () => {
    try {
      await axiosInstance.put(`/admin/hotesses/${edition.user_id}`, {
        base: edition.base || null,
        client_id: edition.client_id || null,
        pause_debut: edition.pause_debut ? Number(edition.pause_debut) : null,
      });
      setEdition(null);
      charger();
    } catch (e) {
      alert(`Erreur : ${e.response?.data?.detail || "l'affectation n'a pas été enregistrée."}`);
    }
  };

  const clientChoisi = edition && clients.find(c => c.id === edition.client_id);

  return (
    <div className="management-page" style={{ padding: '20px' }}>
      <h1 style={{ color: '#2c3e50', margin: 0 }}>Hôtesses</h1>
      <p style={{ color: '#6c757d', marginTop: '6px' }}>
        Chaque hôtesse est affectée à un supermarché. Son accès ne s'ouvre que lorsqu'elle s'y trouve.
        Le supermarché doit avoir sa position GPS enregistrée.
      </p>

      <div className="management-table-container">
        {isLoading ? (
          <p style={{ textAlign: 'center', padding: '40px' }}>Chargement...</p>
        ) : hotesses.length === 0 ? (
          <p className="no-data-message">Aucune hôtesse. Créez d'abord le compte dans « Gestion Utilisateurs » avec le rôle Hotesse.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr><th>Hôtesse</th><th>Email</th><th>Base</th><th>Supermarché</th><th>Pause</th><th>Statut</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {hotesses.map(h => (
                <tr key={h.user_id}>
                  <td><strong>{h.nom}</strong></td>
                  <td>{h.email}</td>
                  <td>{h.base || 'N/A'}</td>
                  <td>{h.client_nom
                    ? <>{h.client_nom}<div style={{ fontSize: '12px', color: '#6c757d' }}>{h.client_zone}</div></>
                    : <span style={{ color: '#dc3545' }}>Non affectée</span>}</td>
                  <td>{h.pause_debut ? `${h.pause_debut}h – ${h.pause_debut + 1}h` : <span style={{ color: '#6c757d' }}>Non renseignée</span>}</td>
                  <td>{h.is_active
                    ? <span className="status-badge valide">Actif</span>
                    : <span className="status-badge rejete">Inactif</span>}</td>
                  <td>
                    <button className="action-button view-button"
                      onClick={() => setEdition({ user_id: h.user_id, nom: h.nom, base: h.base || '', client_id: h.client_id, pause_debut: h.pause_debut || '', recherche: '' })}>
                      Affecter
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {edition && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '24px', width: 'min(520px, 92vw)' }}>
            <h3 style={{ marginTop: 0 }}>Affectation de {edition.nom}</h3>

            <label style={{ fontWeight: 600, fontSize: '13px' }}>Base</label>
            <select value={edition.base} onChange={(e) => setEdition({ ...edition, base: e.target.value })}
              style={{ width: '100%', padding: '8px', margin: '4px 0 14px' }}>
              <option value="">-- Choisir une base --</option>
              <option value="DOUALA">DOUALA</option>
              <option value="YAOUNDE">YAOUNDE</option>
              <option value="BAFOUSSAM">BAFOUSSAM</option>
            </select>

            <label style={{ fontWeight: 600, fontSize: '13px' }}>Pause</label>
            <select value={edition.pause_debut} onChange={(e) => setEdition({ ...edition, pause_debut: e.target.value })}
              style={{ width: '100%', padding: '8px', margin: '4px 0 4px' }}>
              <option value="">-- Non renseignée --</option>
              <option value="13">13h – 14h</option>
              <option value="14">14h – 15h</option>
            </select>
            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '14px' }}>
              Aucun message de présence n'est envoyé pendant la pause. Prise en compte à sa prochaine arrivée de la journée.
            </div>

            <label style={{ fontWeight: 600, fontSize: '13px' }}>Supermarché</label>
            <div style={{ margin: '4px 0 8px', fontSize: '14px' }}>
              {clientChoisi
                ? <>✅ {clientChoisi.nom_client} — {clientChoisi.zone}
                    {(clientChoisi.latitude == null || clientChoisi.longitude == null) &&
                      <div style={{ color: '#dc3545' }}>⚠ Ce point de vente n'a pas de position GPS : l'hôtesse ne pourra pas ouvrir son accès.</div>}</>
                : <span style={{ color: '#6c757d' }}>Aucun</span>}
            </div>
            <input placeholder="Rechercher un supermarché (2 lettres minimum)" value={edition.recherche}
              onChange={(e) => setEdition({ ...edition, recherche: e.target.value })}
              style={{ width: '100%', padding: '8px', boxSizing: 'border-box' }} />
            {suggestions.length > 0 && (
              <div style={{ border: '1px solid #dee2e6', borderRadius: '4px', maxHeight: '220px', overflowY: 'auto', marginTop: '4px' }}>
                {suggestions.map(c => (
                  <div key={c.id} onClick={() => setEdition({ ...edition, client_id: c.id, recherche: '' })}
                    style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #f1f1f1' }}>
                    <strong>{c.nom_client}</strong> <span style={{ color: '#6c757d', fontSize: '12px' }}>{c.zone}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setEdition(null)}
                style={{ padding: '8px 16px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                Annuler
              </button>
              <button onClick={enregistrer}
                style={{ padding: '8px 16px', background: '#0d6efd', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 }}>
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminHotessesPage;
